import { RefObject, useEffect } from "react";
import { postMobileMessage } from "../../../utils/postMobileMessage";

type ViewportWindow = Window & {
  visualViewport?: EventTarget & { height: number; offsetTop: number };
};

const FIELD_GAP = 12;

export function useFocusedFieldScroll(bodyRef: RefObject<HTMLDivElement | null>, enabled: boolean) {
  useEffect(() => {
    const body = bodyRef.current;
    if (!enabled || !body) return;

    postMobileMessage("WEB_SCROLL_MODE", { mode: "internal" });

    const viewport = (window as ViewportWindow).visualViewport;
    let frame: number | undefined;

    const revealField = () => {
      frame = undefined;
      const input = document.activeElement;
      if (!(input instanceof HTMLElement) || !body.contains(input)) return;

      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
        if (input.disabled || input.readOnly) return;
        if (/^(button|submit|reset|checkbox|radio|range|color|file|hidden)$/.test(input.type))
          return;
      } else if (!input.isContentEditable) {
        return;
      }

      const bounds = body.getBoundingClientRect();
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const viewportBottom = viewportTop + (viewport ? viewport.height : window.innerHeight);
      const top = Math.max(bounds.top, viewportTop) + FIELD_GAP;
      const bottom = Math.min(bounds.bottom, viewportBottom) - FIELD_GAP;
      if (bottom <= top) return;

      const group = input.closest("[data-mg-field]");
      let field = (group && body.contains(group) ? group : input).getBoundingClientRect();
      if (field.height > bottom - top) field = input.getBoundingClientRect();

      if (field.bottom > bottom) {
        body.scrollTop += Math.ceil(field.bottom - bottom);
      } else if (field.top < top) {
        body.scrollTop -= Math.ceil(top - field.top);
      }
    };

    const scheduleReveal = () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(revealField);
    };

    body.addEventListener("focusin", scheduleReveal);
    window.addEventListener("resize", scheduleReveal);
    if (viewport) viewport.addEventListener("resize", scheduleReveal);

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      body.removeEventListener("focusin", scheduleReveal);
      window.removeEventListener("resize", scheduleReveal);
      if (viewport) viewport.removeEventListener("resize", scheduleReveal);
      postMobileMessage("WEB_SCROLL_MODE", { mode: "document" });
    };
  }, [bodyRef, enabled]);
}
